(() => {
  const SELECTOR = '[data-brand-story]';
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const mount = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.brandStoryMounted === 'true') return;

    root.dataset.brandStoryMounted = 'true';

    const mediaQuery = window.matchMedia(REDUCED_MOTION);
    const revealItems = [...root.querySelectorAll('[data-reveal]')];
    const parallaxItems = [...root.querySelectorAll('[data-brand-story-parallax]')];
    const comparison = root.querySelector('[data-brand-story-comparison]');
    const scrollCue = root.querySelector('[data-brand-story-scroll]');
    const cleanupTasks = [];

    if (!mediaQuery.matches && 'IntersectionObserver' in window) {
      root.dataset.motionReady = 'true';

      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -8% 0px',
          threshold: 0.12,
        },
      );

      revealItems.forEach((item) => revealObserver.observe(item));
      cleanupTasks.push(() => revealObserver.disconnect());
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    if (!mediaQuery.matches && (parallaxItems.length > 0 || comparison)) {
      let frameId = 0;

      const renderMotion = () => {
        frameId = 0;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        parallaxItems.forEach((item) => {
          const rect = item.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > viewportHeight) return;

          const centerOffset = viewportHeight / 2 - (rect.top + rect.height / 2);
          const translateY = clamp((centerOffset / viewportHeight) * 28, -24, 24);
          item.style.setProperty('--brand-story-parallax-y', `${translateY}px`);
        });

        if (comparison) {
          const rect = comparison.getBoundingClientRect();
          const start = viewportHeight * 1.08;
          const end = -rect.height * 0.12;
          const progress = clamp((start - rect.top) / (start - end), 0, 1);
          root.style.setProperty('--brand-story-comparison-progress', progress.toFixed(3));
        }
      };

      const requestMotionFrame = () => {
        if (frameId) return;
        frameId = window.requestAnimationFrame(renderMotion);
      };

      renderMotion();
      window.addEventListener('scroll', requestMotionFrame, { passive: true });
      window.addEventListener('resize', requestMotionFrame);

      cleanupTasks.push(() => {
        window.removeEventListener('scroll', requestMotionFrame);
        window.removeEventListener('resize', requestMotionFrame);
        if (frameId) window.cancelAnimationFrame(frameId);
      });
    }

    if (scrollCue) {
      const handleScrollCue = (event) => {
        const targetId = scrollCue.getAttribute('href');
        if (!targetId?.startsWith('#')) return;

        const target = document.querySelector(targetId);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          behavior: mediaQuery.matches ? 'auto' : 'smooth',
          block: 'start',
        });
      };

      scrollCue.addEventListener('click', handleScrollCue);
      cleanupTasks.push(() => scrollCue.removeEventListener('click', handleScrollCue));
    }

    root.brandStoryCleanup = () => {
      cleanupTasks.forEach((cleanup) => cleanup());
      delete root.dataset.brandStoryMounted;
      delete root.dataset.motionReady;
      delete root.brandStoryCleanup;
    };
  };

  const mountAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(SELECTOR)) mount(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(mount);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(SELECTOR);
    root?.brandStoryCleanup?.();
  });
})();
